import { inject, Service } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { Api } from '@app/api';
import { IamBackendService } from '@core/providers/backend/iam-backend.service';
import { NavigationService } from '@core/providers/navigation/navigation.service';
import { NotificationService } from '@core/providers/notification/notification.service';
import { ContextUtil } from '@core/utils';
import { AuthService } from './auth.service';
import { LogoutService } from './logout.service';

@Service()
export class LoginGuard {
  private logoutService = inject(LogoutService);
  private authService = inject(AuthService);
  private navigationService = inject(NavigationService);
  private notificationService = inject(NotificationService);
  private iamBackendService = inject(IamBackendService);
  private contextUtil = inject(ContextUtil);

  canActivate(): Observable<boolean> {
    if (!this.authService.authenticated()) {
      return of(true);
    }

    return this.iamBackendService.getUserRedirectInfo().pipe(
      mergeMap(response => {
        if (response && response.uuid) {
          this.redirectUserToProperPage(response.type, response.uuid);
        } else {
          this.logoutService.logOut();
        }

        this.notificationService.hideLoadingPopup();

        return of(false);
      }),
      // Falling through to the login page beats stranding the user on a
      // NavigationError if the lookup fails.
      catchError(() => {
        this.notificationService.hideLoadingPopup();

        return of(true);
      })
    );
  }

  private redirectUserToProperPage(
    type: Api.iam.auth.defaultItem.GetResult['type'],
    uuid: string
  ): void {
    if (type === null) {
      return;
    }
    if (this.contextUtil.isVenueContext(type)) {
      this.navigationService.goToVenueMainScreen(uuid);
    } else if (this.contextUtil.isAccountContext(type)) {
      this.navigationService.goToScoreboards(uuid);
    }
  }
}
